import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Zap, RefreshCw, Play, AlertTriangle, Power, PowerOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Panel de la hidratación proactiva: muestra cuántos mensajes tienen
 * `cuerpo_pendiente=true` (por año) y cuántos quedaron marcados como
 * `error_hidratacion` (tras 3 intentos fallidos). Actualiza cada 15s.
 *
 * También permite disparar un lote on-demand ("Ejecutar ahora") sin
 * esperar al scheduler (cada 2 min), y destildar los errores para
 * reintentar los mensajes marcados como fallidos.
 */
export default function HidratacionProactivaPanel() {
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [resetting, setResetting] = useState(false);
  // iter203 · toggle on/off del worker (persistido en configuracion de Mongo)
  const [habilitado, setHabilitado] = useState(true);
  const [togglingWorker, setTogglingWorker] = useState(false);
  const [workerMeta, setWorkerMeta] = useState({ actualizado_at: null, actualizado_por: null });

  const fetchEstado = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bandeja-unificada/admin/hidratacion-worker/estado`);
      setEstado(r.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHabilitado = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bandeja-unificada/admin/hidratacion-worker/habilitado`);
      setHabilitado(Boolean(r.data?.habilitado));
      setWorkerMeta({
        actualizado_at: r.data?.actualizado_at || null,
        actualizado_por: r.data?.actualizado_por || null,
      });
    } catch (e) {
      // Si el backend no sabe (todavía deployando), asumimos habilitado=true.
      console.error('[hydration habilitado]', e);
    }
  }, []);

  useEffect(() => {
    fetchEstado();
    fetchHabilitado();
    const iv = setInterval(() => { fetchEstado(); fetchHabilitado(); }, 15000);
    return () => clearInterval(iv);
  }, [fetchEstado, fetchHabilitado]);

  const toggleWorker = async () => {
    const nuevoEstado = !habilitado;
    const txt = nuevoEstado
      ? '¿Reactivar el worker de hidratación? Volverá a correr lotes cada ~5 min y consumir ancho de banda IMAP.'
      : '¿Pausar el worker de hidratación? Dejará de descargar cuerpos nuevos en segundo plano hasta que lo reactives. Útil durante picos de tráfico o si Cloudflare empieza a cortar requests.';
    if (!window.confirm(txt)) return;
    setTogglingWorker(true);
    try {
      const r = await axios.post(
        `${API}/bandeja-unificada/admin/hidratacion-worker/habilitado`,
        { habilitado: nuevoEstado },
      );
      setHabilitado(Boolean(r.data?.habilitado));
      setWorkerMeta({
        actualizado_at: r.data?.actualizado_at || null,
        actualizado_por: r.data?.actualizado_por || null,
      });
      toast.success(
        nuevoEstado ? 'Worker reactivado' : 'Worker pausado',
        { description: nuevoEstado
            ? 'La próxima corrida del scheduler ya lo ejecuta.'
            : 'El scheduler saltea los próximos lotes hasta que lo reactives. Sin reiniciar el servidor.',
        },
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cambiando el estado del worker');
    }
    setTogglingWorker(false);
  };

  const runOnce = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/bandeja-unificada/admin/hidratacion-worker/run-once?limit=10`);
      const s = r.data || {};
      toast.success(
        `Lote ejecutado · ok=${s.ok || 0} · retry=${s.retry || 0} · error=${s.error || 0}`,
        { description: `Procesados ${s.procesados || 0} mensajes.` }
      );
      await fetchEstado();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error ejecutando lote');
    }
    setRunning(false);
  };

  const reintentarErrores = async () => {
    if (!window.confirm('¿Volver a poner en cola los mensajes marcados con error_hidratacion?\n\nSe reiniciará su contador de intentos.')) return;
    setResetting(true);
    try {
      const r = await axios.post(`${API}/bandeja-unificada/admin/hidratacion-worker/reintentar-errores`);
      toast.success(`${r.data?.reseteados || 0} mensajes reencolados`);
      await fetchEstado();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error reencolando');
    }
    setResetting(false);
  };

  const pendientes = estado?.pendientes ?? 0;
  const conError = estado?.con_error ?? 0;
  const porAnio = estado?.por_anio || {};
  const aniosOrdenados = Object.keys(porAnio).sort().reverse();

  return (
    <Card data-testid="hidratacion-proactiva-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-emerald-600" /> Hidratación proactiva de cuerpos IMAP
            </CardTitle>
            <p className="text-xs text-gray-600 mt-1">
              Worker en background: descarga cuerpo + adjuntos de los mensajes migrados en lotes de hasta 10 cada 2 minutos.
              Respeta el semáforo IMAP (espera si hay sync o hidratación on-demand corriendo). Los más nuevos primero.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <div className="flex gap-2 flex-wrap justify-end">
              <Button
                size="sm"
                variant={habilitado ? "outline" : "default"}
                onClick={toggleWorker}
                disabled={togglingWorker}
                className={habilitado
                  ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                data-testid="btn-toggle-hydration-worker"
                title={habilitado
                  ? "Pausar el worker (útil durante picos de tráfico o si Cloudflare corta requests)"
                  : "Reactivar el worker"}
              >
                {togglingWorker ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {habilitado ? 'Pausando...' : 'Activando...'}</>
                ) : habilitado ? (
                  <><PowerOff className="w-4 h-4 mr-1" /> Pausar worker</>
                ) : (
                  <><Power className="w-4 h-4 mr-1" /> Reactivar worker</>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchEstado}
                disabled={loading}
                data-testid="btn-refresh-hydrate"
                title="Actualizar contadores"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
              </Button>
              <Button
                size="sm"
                onClick={runOnce}
                disabled={running || pendientes === 0 || !habilitado}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-run-hydrate-once"
                title={!habilitado
                  ? "Reactivá el worker primero para ejecutar un lote"
                  : "Ejecutar un lote ahora (sin esperar al scheduler)"}
              >
                {running ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Ejecutando...</>
                ) : (
                  <><Play className="w-4 h-4 mr-1" /> Ejecutar ahora</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!habilitado && (
          <div
            className="mb-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2"
            data-testid="banner-worker-pausado"
          >
            <PowerOff className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm flex-1">
              <p className="font-semibold text-amber-900">Worker pausado</p>
              <p className="text-amber-800">
                El scheduler NO ejecuta lotes de hidratación hasta que lo reactives. Los contadores pueden
                seguir creciendo porque los syncs IMAP agregan nuevos mensajes a la cola, pero NO se procesan.
                {workerMeta.actualizado_at && (
                  <>
                    {' '}Pausado por <strong>{workerMeta.actualizado_por || 'admin'}</strong>
                    {' '}el {new Date(workerMeta.actualizado_at).toLocaleString('es-AR')}.
                  </>
                )}
              </p>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Contador principal */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Mensajes pendientes de hidratar</p>
              <p className="text-4xl font-bold text-emerald-900 mt-1" data-testid="count-pendientes">
                {pendientes.toLocaleString('es-AR')}
              </p>
              {pendientes > 0 ? (
                <p className="text-xs text-emerald-700 mt-1">
                  ETA aproximado: {Math.ceil(pendientes / 10 * 2)} min al ritmo actual (lote 10 / 2 min)
                </p>
              ) : (
                <p className="text-xs text-emerald-700 mt-1">✓ No hay pendientes. Todo hidratado.</p>
              )}
            </div>

            {/* Errores */}
            <div className={`rounded-lg border p-4 ${conError > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
              <p className={`text-xs uppercase tracking-wide font-semibold flex items-center gap-1 ${conError > 0 ? 'text-red-700' : 'text-gray-600'}`}>
                {conError > 0 && <AlertTriangle className="w-3.5 h-3.5" />}
                Con error (3 intentos fallidos)
              </p>
              <p className={`text-4xl font-bold mt-1 ${conError > 0 ? 'text-red-900' : 'text-gray-500'}`} data-testid="count-errores">
                {conError.toLocaleString('es-AR')}
              </p>
              {conError > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={reintentarErrores}
                  disabled={resetting}
                  className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                  data-testid="btn-reintentar-errores"
                >
                  {resetting ? 'Reencolando…' : `Reintentar los ${conError}`}
                </Button>
              )}
              {conError === 0 && (
                <p className="text-xs text-gray-500 mt-1">Sin mensajes fallidos.</p>
              )}
            </div>

            {/* Desglose por año */}
            {aniosOrdenados.length > 0 && (
              <div className="md:col-span-2 rounded-lg border border-gray-200 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-600 font-semibold mb-2">Pendientes por año (prioridad DESC)</p>
                <div className="flex flex-wrap gap-2">
                  {aniosOrdenados.map((anio) => (
                    <span
                      key={anio}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs"
                      data-testid={`pendientes-anio-${anio}`}
                    >
                      <strong className="text-emerald-800">{anio}</strong>
                      <span className="text-emerald-700">{porAnio[anio].toLocaleString('es-AR')}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
